import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from 'shared/contexts/AuthContext';
import { GlobalLoaderProvider } from 'shared/contexts/GlobalLoaderContext';
import { PageTitleProvider } from 'shared/contexts/PageTitleContext';
import { CookieConsent, PrivateRoute, Layout, ThemeWrapper, RestrictedRoute, CompanyContextGuard, UserTwoFactorSetup } from 'shared/components';
import type { CookieConsents } from 'shared/components/CookieConsent';
import { ModuleGuard } from 'shared/components/ModuleGuard';
import { SessionExpiredModal } from 'shared/components/SessionExpiredModal';
import { NetworkBlockedModal } from 'shared/components/NetworkBlockedModal';
import { Box, CircularProgress } from '@mui/material';
import { trackingService } from 'shared/services/trackingService';
import { queryClient } from 'shared/hooks';
import { UserRole } from 'shared/types';
import {
  BLOCK_NON_PLATFORM,
  BLOCK_NON_APPROVER,
  BLOCK_NON_CLIENT_STAFF,
  BLOCK_NON_SECURITY,
  BLOCK_NON_MARKETPLACE,
  BLOCK_NON_GUEST_ACCESS,
  BLOCK_NON_LIBRARY,
  BLOCK_NON_LIBRARY_READ,
  BLOCK_NON_CONTRACTOR_MGMT,
  BLOCK_NON_TEAMS,
  BLOCK_NON_SUPER_ADMIN,
  BLOCK_NON_REMOVAL_REQUESTS,
} from 'shared/constants/routeAccess';
import { NotFound } from 'features/NotFound';
import { RoleBasedRedirect } from 'shared/components/RoleBasedRedirect';
import { ErrorBoundary } from 'shared/components/ErrorBoundary';

/* ==================== EAGER IMPORTS (public routes / first paint) ==================== */
import { Login } from 'features/auth/Login';
import { RegisterWithInvitation } from 'features/auth/RegisterWithInvitation';
import { ForgotPassword } from 'features/auth/ForgotPassword';
import { ResetPassword } from 'features/auth/ResetPassword';
import { AcceptInvitation } from 'features/auth/AcceptInvitation';
import { Unauthorized } from 'features/auth/Unauthorized';
import { SetupTwoFactorWithToken } from 'features/auth/SetupTwoFactorWithToken';
import { TermsAndConditions } from 'features/legal/TermsAndConditions';
import { PrivacyPolicy } from 'features/legal/PrivacyPolicy';
import { CookiePolicy } from 'features/legal/CookiePolicy';

/* ==================== LAZY IMPORTS (protected routes) ==================== */
// Note: the bare `lazy` shim used to wrap React.lazy is no longer
// needed — every route now uses `lazyNamed` (CLAUDE.md convention so
// chunks have readable names in devtools).
// React's own `LazyExoticComponent` constraint requires `ComponentType<any>`,
// so the named-module lookup uses `any` here intentionally; a generic shim
// fights the upstream typing without buying any real safety.
 
const lazyNamed = (factory: () => Promise<any>, name: string): React.LazyExoticComponent<React.ComponentType<any>> =>
   
  React.lazy(() => factory().then((mod: any) => ({ default: mod[name] })));

// General
const Profile = lazyNamed(() => import('features/general/Profile'), 'Profile');
const Settings = lazyNamed(() => import('features/general/Settings'), 'Settings');
const UserProfileBadgeModal = lazyNamed(() => import('features/general/UserProfileBadgeModal'), 'UserProfileBadgeModal');
const NotificationsPage = lazyNamed(() => import('features/notifications/NotificationsPage'), 'NotificationsPage');
const Messages = lazyNamed(() => import('features/messages/Messages'), 'Messages');
const MarketplacePage = lazyNamed(() => import('features/marketplace/MarketplacePage'), 'MarketplacePage');
const CompanyInformacionPage = lazyNamed(() => import('features/companies/CompanyInformacionPage'), 'CompanyInformacionPage');
const CompanyDocumentsPage = lazyNamed(() => import('features/company-documents/CompanyDocumentsPage'), 'CompanyDocumentsPage');
const CompanyCoursesPage = lazyNamed(() => import('features/companies/CompanyCoursesPage'), 'CompanyCoursesPage');
const CompanyHistorialPage = lazyNamed(() => import('features/companies/CompanyHistorialPage'), 'CompanyHistorialPage');
const MyProviders = lazyNamed(() => import('features/companies/MyProviders'), 'MyProviders');
const ProviderInMyCompany = lazyNamed(() => import('features/companies/ProviderInMyCompany'), 'ProviderInMyCompany');
const ProviderRegistration = lazyNamed(() => import('features/auth/ProviderRegistration'), 'ProviderRegistration');
const CompanyRegistration = lazyNamed(() => import('features/auth/CompanyRegistration'), 'CompanyRegistration');
const QuestionnairePage = lazyNamed(() => import('features/questionnaire/QuestionnairePage'), 'QuestionnairePage');

// Companies
const Companies = lazyNamed(() => import('features/companies/Companies'), 'Companies');
const CompanyForm = lazyNamed(() => import('features/companies/CompanyForm'), 'CompanyForm');
const CompanyDetail = lazyNamed(() => import('features/companies/CompanyDetail'), 'CompanyDetail');
const CompanyDepartmentsPage = lazyNamed(() => import('features/companies/CompanyDepartmentsPage'), 'CompanyDepartmentsPage');
const UsersAndVerificationsTabs = lazyNamed(() => import('features/companies/UsersAndVerificationsTabs'), 'UsersAndVerificationsTabs');
const CompanyVerificationsPage = lazyNamed(() => import('features/companies/CompanyVerificationsPage'), 'CompanyVerificationsPage');
const UserVerificationsPage = lazyNamed(() => import('features/companies/UserVerificationsPage'), 'UserVerificationsPage');
const ProviderVerificationsPage = lazyNamed(() => import('features/companies/ProviderVerificationsPage'), 'ProviderVerificationsPage');
const MyClients = lazyNamed(() => import('features/companies/MyClients'), 'MyClients');

// Users & Teams
const Users = lazyNamed(() => import('features/users/Users'), 'Users');
const AllUsersDashboard = lazyNamed(() => import('features/users/AllUsersDashboard'), 'AllUsersDashboard');
const InvitationsList = lazyNamed(() => import('features/users/InvitationsList'), 'InvitationsList');
const UserForm = lazyNamed(() => import('features/users/UserForm'), 'UserForm');
const CompanyUserForm = lazyNamed(() => import('features/users/CompanyUserForm'), 'CompanyUserForm');
const CompanyUserDetail = lazyNamed(() => import('features/users/CompanyUserDetail'), 'CompanyUserDetail');
const InvitationCodes = lazyNamed(() => import('features/users/InvitationCodes'), 'InvitationCodes');
const MyTeams = lazyNamed(() => import('features/users/MyTeams'), 'MyTeams');
const TeamMembers = lazyNamed(() => import('features/users/TeamMembers'), 'TeamMembers');
const BatchUserRegistration = lazyNamed(() => import('features/users/batch-registration/BatchUserRegistration'), 'BatchUserRegistration');

// Service Management & Library
const LibraryTabs = lazyNamed(() => import('features/service-management/LibraryTabs'), 'LibraryTabs');
const ServiceCards = lazyNamed(() => import('features/service-management/service-cards/ServiceCards'), 'ServiceCards');
const ServiceCardDetail = lazyNamed(() => import('features/service-management/service-cards/ServiceCardDetail'), 'ServiceCardDetail');
const ServiceCardForm = lazyNamed(() => import('features/service-management/service-cards/ServiceCardForm'), 'ServiceCardForm');
const InitialEvaluations = lazyNamed(() => import('features/service-management/initial-evaluations/InitialEvaluations'), 'InitialEvaluations');
const InitialEvaluationDetail = lazyNamed(() => import('features/service-management/initial-evaluations/InitialEvaluationDetail'), 'InitialEvaluationDetail');
const InitialEvaluationCreateFlow = lazyNamed(() => import('features/service-management/initial-evaluations/InitialEvaluationCreateFlow'), 'InitialEvaluationCreateFlow');
const CriticalTaskPermits = lazyNamed(() => import('features/service-management/critical-task-permits/CriticalTaskPermits'), 'CriticalTaskPermits');
const CriticalTaskPermitDetail = lazyNamed(() => import('features/service-management/critical-task-permits/CriticalTaskPermitDetail'), 'CriticalTaskPermitDetail');
const CriticalTaskPermitCreateFlow = lazyNamed(() => import('features/service-management/critical-task-permits/CriticalTaskPermitCreateFlow'), 'CriticalTaskPermitCreateFlow');
const TemplateDetail = lazyNamed(() => import('features/permits/TemplateDetail'), 'TemplateDetail');
const FormEditor = lazyNamed(() => import('features/forms/FormEditor'), 'FormEditor');
const FormDetail = lazyNamed(() => import('features/forms/FormDetail'), 'FormDetail');

// Contractors
const Contractors = lazyNamed(() => import('features/contractors/Contractors'), 'Contractors');
const ContractorForm = lazyNamed(() => import('features/contractors/ContractorForm'), 'ContractorForm');
const ContractorDetail = lazyNamed(() => import('features/contractors/ContractorDetail'), 'ContractorDetail');
const ContractorSearch = lazyNamed(() => import('features/contractors/ContractorSearch'), 'ContractorSearch');
const ContractorProgressPage = lazyNamed(() => import('features/contractors/ContractorProgress'), 'ContractorProgressPage');
const SupervisedContractors = lazyNamed(() => import('features/contractors/SupervisedContractors'), 'SupervisedContractors');
const SupervisedContractorForm = lazyNamed(() => import('features/contractors/SupervisedContractorForm'), 'SupervisedContractorForm');
const SupervisedContractorDetail = lazyNamed(() => import('features/contractors/SupervisedContractorDetail'), 'SupervisedContractorDetail');
const ContractorDashboard = lazyNamed(() => import('features/contractors/ContractorDashboard'), 'ContractorDashboard');
const ManageAvailableUsers = lazyNamed(() => import('features/contractors/ManageAvailableUsers'), 'ManageAvailableUsers');

// Courses
const Courses = lazyNamed(() => import('features/courses/Courses'), 'Courses');
const CourseEnrollments = lazyNamed(() => import('features/courses/CourseEnrollments'), 'CourseEnrollments');
const BulkSync = lazyNamed(() => import('features/general/BulkSync'), 'BulkSync');

// Marketplace — disabled, planned for future
// const Services = lazyNamed(() => import('features/marketplace/Services'), 'Services');
// const ServiceForm = lazyNamed(() => import('features/marketplace/ServiceForm'), 'ServiceForm');
// const ServiceDetail = lazyNamed(() => import('features/marketplace/ServiceDetail'), 'ServiceDetail');
// const WorkRequests = lazyNamed(() => import('features/marketplace/WorkRequests'), 'WorkRequests');
// const WorkRequestForm = lazyNamed(() => import('features/marketplace/WorkRequestForm'), 'WorkRequestForm');
// const WorkRequestDetail = lazyNamed(() => import('features/marketplace/WorkRequestDetail'), 'WorkRequestDetail');
// const WorkOrders = lazyNamed(() => import('features/marketplace/WorkOrders'), 'WorkOrders');
// const Inspections = lazyNamed(() => import('features/marketplace/Inspections'), 'Inspections');

// Tasks
const TaskDetail = lazyNamed(() => import('features/tasks/TaskDetail'), 'TaskDetail');

// User Verifications
const UserVerificationsDashboard = lazyNamed(() => import('features/user-verifications/UserVerificationsDashboard'), 'UserVerificationsDashboard');
const BulkVerificationWizard = lazyNamed(() => import('features/bulk-verifications/BulkVerificationWizard'), 'BulkVerificationWizard');
const BulkVerificationApprovalInbox = lazyNamed(() => import('features/bulk-verifications/BulkVerificationApprovalInbox'), 'BulkVerificationApprovalInbox');
const BulkVerificationDetail = lazyNamed(() => import('features/bulk-verifications/BulkVerificationDetail'), 'BulkVerificationDetail');

// Approver
const ApproverDashboard = lazyNamed(() => import('features/approver/ApproverDashboard'), 'ApproverDashboard');
const ApproverCompanyDetail = lazyNamed(() => import('features/approver/ApproverCompanyDetail'), 'ApproverCompanyDetail');
const ApproverWorkPermits = lazyNamed(() => import('features/approver/ApproverWorkPermits'), 'ApproverWorkPermits');
const ApproverWorkPermitDetail = lazyNamed(() => import('features/approver/ApproverWorkPermitDetail'), 'ApproverWorkPermitDetail');

// Security (Guard)
const SecurityUsers = lazyNamed(() => import('features/security/SecurityUsers'), 'SecurityUsers');
const QrScanner = lazyNamed(() => import('features/security/QrScanner'), 'QrScanner');
const SecurityInicioPage = lazyNamed(() => import('features/security/SecurityInicioPage'), 'SecurityInicioPage');
const SecurityConsultaPage = lazyNamed(() => import('features/security/SecurityConsultaPage'), 'SecurityConsultaPage');

// Admin
const SuperAdminDashboard = lazyNamed(() => import('features/admin/SuperAdminDashboard'), 'SuperAdminDashboard');
const MyPortfolioDashboard = lazyNamed(() => import('features/portfolio/MyPortfolioDashboard'), 'MyPortfolioDashboard');
const TransferRequestsPage = lazyNamed(() => import('features/transfers/TransferRequestsPage'), 'TransferRequestsPage');
const UserRemovalRequestsPage = lazyNamed(() => import('features/users/UserRemovalRequestsPage'), 'UserRemovalRequestsPage');
const ReassignAfterRemovalPage = lazyNamed(() => import('features/users/ReassignAfterRemovalPage'), 'ReassignAfterRemovalPage');
const SupervisorTransfersPage = lazyNamed(() => import('features/supervisor-transfers/SupervisorTransfersPage'), 'SupervisorTransfersPage');
// New super_admin sidebar (stub pages — filled in in subsequent steps)
const InicioPage = lazyNamed(() => import('features/super-admin/InicioPage'), 'InicioPage');
const ResumenPage = lazyNamed(() => import('features/super-admin/ResumenPage'), 'ResumenPage');
const EnProcesoPage = lazyNamed(() => import('features/super-admin/EnProcesoPage'), 'EnProcesoPage');
const GlobalVerificationsPage = lazyNamed(() => import('features/super-admin/VerificationsPage'), 'VerificationsPage');
const HistorialPage = lazyNamed(() => import('features/super-admin/HistorialPage'), 'HistorialPage');
const NotificacionesPageNew = lazyNamed(() => import('features/super-admin/NotificacionesPage'), 'NotificacionesPage');
const CapacitacionesPage = lazyNamed(() => import('features/super-admin/CapacitacionesPage'), 'CapacitacionesPage');
const ConfiguracionPage = lazyNamed(() => import('features/super-admin/ConfiguracionPage'), 'ConfiguracionPage');
const UserVerificationAuditLog = lazyNamed(() => import('features/admin/UserVerificationAuditLog'), 'UserVerificationAuditLog');

// Client Staff
const ClientStaffDashboard = lazyNamed(() => import('features/client-staff/ClientStaffDashboard'), 'ClientStaffDashboard');
const StaffInicio = lazyNamed(() => import('features/client-staff/StaffInicio'), 'StaffInicio');
const StaffConsulta = lazyNamed(() => import('features/client-staff/StaffConsulta'), 'StaffConsulta');
const StaffTareas = lazyNamed(() => import('features/client-staff/StaffTareas'), 'StaffTareas');
const StaffHistorial = lazyNamed(() => import('features/client-staff/StaffHistorial'), 'StaffHistorial');
const StaffTeam = lazyNamed(() => import('features/client-staff/StaffTeam'), 'StaffTeam');

// Settings
const CompanySettings = lazyNamed(() => import('features/settings/CompanySettings'), 'CompanySettings');
// Billing-related lazy imports retired while billing is paused for the
// pilot. The route paths still exist (they Navigate to "/") so deep links
// don't break, but the components themselves don't ship. Restore these
// imports + their Route elements when billing comes back online.
// const BillingInvoices = lazyNamed(() => import('features/billing/BillingInvoices'), 'BillingInvoices');
// const Walkthrough = lazyNamed(() => import('features/billing/Walkthrough'), 'Walkthrough');
// const MyBilling = lazyNamed(() => import('features/billing/MyBilling'), 'MyBilling');

// QA (super_admin only)
const QAChecklist = lazyNamed(() => import('features/qa/QaChecklist'), 'QaChecklist');

// Incident Reports
const IncidentReports = lazyNamed(() => import('features/incident-reports/IncidentReports'), 'IncidentReports');

// Other standalone features
const AttentionCalls = lazyNamed(() => import('features/attention-calls/AttentionCalls'), 'AttentionCalls');
// GuestVisits is also surfaced as a top-level page under /my-company/visitas
// (and /companies/:companyId/visitas for platform roles); it remains embedded
// inside the Usuarios "Invitados" tab for the legacy UI.
const GuestVisits = lazyNamed(() => import('features/guest-visits/GuestVisits'), 'GuestVisits');
const GuestSearch = lazyNamed(() => import('features/guest-visits/GuestSearch'), 'GuestSearch');
const GuestVisitVerify = lazyNamed(() => import('features/guest-visits/GuestVisitVerify'), 'GuestVisitVerify');
const SupportTickets = lazyNamed(() => import('features/support-tickets/SupportTickets'), 'SupportTickets');
const ActivityLog = lazyNamed(() => import('features/activity-log/ActivityLog'), 'ActivityLog');
const HelpCenter = lazyNamed(() => import('features/help/HelpCenter'), 'HelpCenter');
const GuideAdmin = lazyNamed(() => import('features/guide-admin/GuideAdmin'), 'GuideAdmin');

/**
 * /notificaciones router — branches by role:
 *   • super_admin & safety_staff → new TO-DO/notifications board (NotificacionesPageNew)
 *   • everyone else (clients, contractors, security, approvers) → legacy notification center
 *
 * Same URL for everyone so deep-links work and we don't have to maintain two paths.
 */
const NotificacionesRouter: React.FC = () => {
  const { hasRole } = useAuth();
  if (hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF])) {
    return <NotificacionesPageNew />;
  }
  return <NotificationsPage />;
};

/**
 * Forwards `/accept-invitation?code=XXX` (the URL shape historic emails
 * generate) to the canonical `/register/invitation/:code` route.
 */
function AcceptInvitationRedirect() {
  const [params] = useSearchParams();
  const code = params.get('code');
  if (!code) return <Navigate to="/login" replace />;
  return <Navigate to={`/register/invitation/${code}`} replace />;
}

/* ==================== APP COMPONENT ==================== */

function AppContent() {
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [networkBlocked, setNetworkBlocked] = useState(false);
  const { sessionExpired, logout, clearSessionExpired } = useAuth();

  useEffect(() => {
    trackingService.initialize();
    const hasConsent = localStorage.getItem('cookie-consent');
    if (!hasConsent) {
      setShowCookieConsent(true);
    }
  }, []);

  useEffect(() => {
    const handleNetworkBlocked = () => setNetworkBlocked(true);
    window.addEventListener('network-request-blocked', handleNetworkBlocked);
    return () => window.removeEventListener('network-request-blocked', handleNetworkBlocked);
  }, []);

  const handleCookieConsent = (consents: CookieConsents) => {
    trackingService.updateConsent(consents);
    setShowCookieConsent(false);
  };

  const handleSessionExpiredLogin = () => {
    clearSessionExpired();
    logout();
    // Navigation to /login will happen automatically via PrivateRoute
  };

  return (
    <Router>
      <GlobalLoaderProvider>
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
      <Routes>
        {/* ==================== PUBLIC ROUTES ==================== */}
        <Route path="/login" element={<Login />} />
        {/* Self-registration disabled — only admins can register users */}
        <Route path="/register/invitation/:code" element={<RegisterWithInvitation />} />
        {/* Legacy email link shape: /accept-invitation?code=XXX → forward to canonical path. */}
        <Route path="/accept-invitation" element={<AcceptInvitationRedirect />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/aceptar-invitacion" element={<AcceptInvitation />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/setup-2fa/:token" element={<SetupTwoFactorWithToken />} />
        <Route path="/setup-2fa" element={<SetupTwoFactorWithToken />} />
        <Route path="/register/provider" element={<ProviderRegistration />} />
        <Route path="/register/company" element={<CompanyRegistration />} />
        {/* Public questionnaire — the script renders the full page itself */}
        <Route path="/diagnostico" element={<QuestionnairePage />} />
        {/* Public profile badge — intentionally unauthenticated for QR code sharing */}
        <Route path="/profile/:id" element={<UserProfileBadgeModal open={true} onClose={()=>{}}/>} />
        
        {/* Legal */}
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />

        {/* ==================== PROTECTED ROUTES ==================== */}
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>

          {/* Dashboard & General */}
          <Route index element={<RoleBasedRedirect />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
          {/* Billing/walkthrough is paused for the pilot — every company runs
              on Business with no expiry, so the activation walkthrough has
              nothing to gate. Deep link returns the user to their landing. */}
          <Route path="walkthrough" element={<Navigate to="/" replace />} />
          {/* Legacy /notifications path — redirect to the canonical /notificaciones,
              preserving any query string (e.g. ?chat=open) so deep links keep working. */}
          <Route
            path="notifications"
            element={<Navigate to={`/notificaciones${typeof window !== 'undefined' ? window.location.search : ''}`} replace />}
          />
          <Route path="ayuda" element={<HelpCenter />} />
          {/* Dedicated Mensajes route — renders the Messages feature directly so
              the sidebar's "Mensajes" entry doesn't dump users on the Notifications
              page. The legacy /notificaciones?chat=open deep-link still works. */}
          <Route path="mensajes" element={<Messages />} />
          {/* Mi Equipo was folded into Usuarios — keep redirect for any stale links. */}
          <Route path="mi-equipo" element={<Navigate to="/my-company/team" replace />} />
          {/* Marketplace — plan-gated (Business+) and provider-opt-out aware
              on the BE side. Sidebar entry hides the link for ineligible
              users; this route is reachable only via direct URL otherwise. */}
          <Route path="marketplace" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_MARKETPLACE]}><ModuleGuard module="marketplace"><MarketplacePage /></ModuleGuard></RestrictedRoute>} />
          <Route path="guia" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><GuideAdmin /></RestrictedRoute>} />
          <Route path="security" element={<UserTwoFactorSetup />} />
          <Route path="dashboard" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SUPER_ADMIN]}><SuperAdminDashboard /></RestrictedRoute>} />
          <Route path="portfolio" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><MyPortfolioDashboard /></RestrictedRoute>} />
          <Route path="transfer-requests" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><TransferRequestsPage /></RestrictedRoute>} />
          <Route path="solicitudes-remocion" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_REMOVAL_REQUESTS]}><UserRemovalRequestsPage /></RestrictedRoute>} />
          <Route path="users/:userId/companies/:companyId/reassign" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><ReassignAfterRemovalPage /></RestrictedRoute>} />
          {/* Bulk verification approval queue (global — Super Admin + Soporte Monitor) */}
          <Route path="bulk-verifications" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><BulkVerificationApprovalInbox /></RestrictedRoute>} />
          {/* Detail route: platform roles (review/decide) + company_admin (view their own
              submission's status) — reusing BLOCK_NON_REMOVAL_REQUESTS, the existing constant
              for this exact "platform + company_admin" shape. Audit fix 2026-09-07: this route
              had no RestrictedRoute at all; BE already enforces access (403 for unauthorized
              callers), so this closes a defense-in-depth gap rather than a live vulnerability. */}
          <Route path="bulk-verifications/:id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_REMOVAL_REQUESTS]}><BulkVerificationDetail /></RestrictedRoute>} />

          {/* Platform sidebar (super_admin + safety_staff "Soporte Monitor") */}
          <Route path="inicio" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><InicioPage /></RestrictedRoute>} />
          <Route path="resumen" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><ResumenPage /></RestrictedRoute>} />
          <Route path="en-proceso" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><EnProcesoPage /></RestrictedRoute>} />
          <Route path="verifications" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><GlobalVerificationsPage /></RestrictedRoute>} />
          <Route path="historial" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><HistorialPage /></RestrictedRoute>} />
          {/* Notifications: platform users get the new TO-DO board (NotificacionesPageNew),
              everyone else gets the original notification center. Single canonical URL. */}
          <Route path="notificaciones" element={<NotificacionesRouter />} />
          <Route path="capacitaciones" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><CapacitacionesPage /></RestrictedRoute>} />
          <Route path="configuracion" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><ConfiguracionPage /></RestrictedRoute>} />
          <Route path="qa-checklist" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SUPER_ADMIN]}><QAChecklist /></RestrictedRoute>} />

          {/* === CONTEXT: MY COMPANY (The "Clean" URL for Managers) ===
            These routes infer the companyId from the logged-in user's context.
            No ID required in the URL. Restricted from CLIENT_APPROVER role.
          */}
          <Route path="my-company" element={<RestrictedRoute blockedRoles={[UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Outlet /></RestrictedRoute>}>
            <Route index element={<CompanyDetail />} />

            {/* Team Management — Usuarios + Verificaciones share a
                tabbed shell so users can swap between the two without
                losing company context. Each URL still maps to its tab
                so deep links and browser back/forward keep working. */}
            <Route path="team" element={<UsersAndVerificationsTabs activeTab="usuarios" />} />
            <Route path="team/new" element={<CompanyUserForm />} />
            <Route path="team/:id" element={<CompanyUserDetail />} />
            <Route path="team/:id/edit" element={<CompanyUserForm />} />
            {/* Invitaciones tab — same shell as team/verifications, lists
                users invited but not yet activated (distinct from the
                code-based /my-company/invitations screen). */}
            <Route path="invitaciones" element={<UsersAndVerificationsTabs activeTab="invitaciones" />} />
            {/* Batch CSV registration — same component as the
                /companies/:id/batch-registration mount; company_admin
                needs its own URL because the platform branch is blocked
                for non-platform roles. */}
            <Route path="batch-registration" element={<BatchUserRegistration />} />
            <Route path="invitations" element={<InvitationCodes />} />
            {/* Visitas — top-level entry in the company dashboard. Mirrors
                the embedded /my-company/team Invitados tab but with its own
                URL + sidebar slot. */}
            <Route path="visitas" element={<GuestVisits />} />

            {/* Operations */}
            <Route path="departments" element={<CompanyDepartmentsPage />} />
            <Route path="cursos" element={<ModuleGuard module="capacitaciones"><CompanyCoursesPage /></ModuleGuard>} />
            <Route path="verifications" element={<CompanyVerificationsPage />} />
            <Route path="verifications/usuarios" element={<UserVerificationsPage />} />
            <Route path="verifications/proveedores" element={<ProviderVerificationsPage />} />
            <Route path="verifications/bulk/new" element={<BulkVerificationWizard />} />
            {/* Proveedores + Clientes — same pages as the platform
                /companies/:id/(proveedores|clientes) routes, just
                mounted here for company_admin so they can drill into
                their relationships without bouncing through /my-providers. */}
            <Route path="proveedores" element={<MyProviders />} />
            <Route path="proveedores/:providerId" element={<ProviderInMyCompany />} />
            <Route path="clientes" element={<MyClients />} />
            <Route path="settings" element={<RestrictedRoute blockedRoles={[UserRole.CLIENT_STAFF, UserRole.SECURITY]}><CompanySettings /></RestrictedRoute>} />
            {/* Edit own company — company_admin can edit basic info of
                their own company. The BE PUT /api/companies/:id already
                allows company_admin scope. */}
            <Route path="edit" element={<CompanyForm />} />
            {/* Self-service billing paused for the pilot — deep links bounce
                to landing instead of rendering the invoice/upgrade UI. */}
            <Route path="billing" element={<Navigate to="/" replace />} />
            {/* Library — Fichas / Evaluaciones / Permisos for cliente
                company_admins. ModuleGuard hides it when workPermits is
                disabled per company. */}
            <Route path="library" element={<ModuleGuard module="workPermits"><LibraryTabs /></ModuleGuard>} />
            <Route path="incident-reports" element={<IncidentReports />} />
            <Route path="attention-calls" element={<AttentionCalls />} />
            <Route path="support-tickets" element={<SupportTickets />} />
            <Route path="logs" element={<ActivityLog />} />
            {/* Aggregate sidebar pages for company_admin */}
            <Route path="informacion" element={<CompanyInformacionPage />} />
            <Route path="historial" element={<CompanyHistorialPage />} />
            <Route path="documentos" element={<CompanyDocumentsPage />} />
            <Route path="supervisor-transfers" element={<SupervisorTransfersPage />} />
          </Route>

          {/* === Standalone list pages (no company context) === */}
          <Route path="logs" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><ActivityLog /></RestrictedRoute>} />
          {/* Platform-level Facturas pausada con el resto de billing. */}
          <Route path="billing" element={<Navigate to="/" replace />} />
          <Route path="attention-calls" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><AttentionCalls /></RestrictedRoute>} />
          <Route path="support-tickets" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><SupportTickets /></RestrictedRoute>} />
          <Route path="guest-visits/verify/:code" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_GUEST_ACCESS]}><GuestVisitVerify /></RestrictedRoute>} />
          {/* manage-available-users: provider admins only. Route gate allows
              company_admin through; the page + BE enforce that the caller's
              home company is `proveedor` (post role unification). */}
          <Route path="manage-available-users" element={<RestrictedRoute blockedRoles={[UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><ManageAvailableUsers /></RestrictedRoute>} />

          {/* === CONTEXT: LIBRARY & PERMITS (Context Aware) ===
            Routes for managing the Safety Library.
            Only super_admin, safety_staff, and company_admin can access.
          */}
          <Route path="library">
            <Route index element={<RestrictedRoute blockedRoles={[UserRole.CLIENT_STAFF, UserRole.SECURITY]} allowWorkDocumentManagers><LibraryTabs /></RestrictedRoute>} />

            {/* Service Cards (requires workPermits module) */}
            <Route path="service-cards/new" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]} allowWorkDocumentManagers><ModuleGuard module="workPermits"><ServiceCardForm /></ModuleGuard></RestrictedRoute>} />
            <Route path="service-cards/:id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY_READ]}><ModuleGuard module="workPermits"><ServiceCardDetail /></ModuleGuard></RestrictedRoute>} />
            <Route path="service-cards/:id/edit" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]} allowWorkDocumentManagers><ModuleGuard module="workPermits"><ServiceCardForm /></ModuleGuard></RestrictedRoute>} />

            {/* Initial Evaluations (requires workPermits module) */}
            <Route path="evaluations/new" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]} allowWorkDocumentManagers><ModuleGuard module="workPermits"><InitialEvaluationCreateFlow /></ModuleGuard></RestrictedRoute>} />
            <Route path="evaluations/:id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY_READ]}><ModuleGuard module="workPermits"><InitialEvaluationDetail /></ModuleGuard></RestrictedRoute>} />
            <Route path="evaluations/:id/edit" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]} allowWorkDocumentManagers><ModuleGuard module="workPermits"><InitialEvaluationCreateFlow /></ModuleGuard></RestrictedRoute>} />

            {/* Critical Task Permits (requires workPermits module) */}
            <Route path="permits/new" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]} allowWorkDocumentManagers><ModuleGuard module="workPermits"><CriticalTaskPermitCreateFlow /></ModuleGuard></RestrictedRoute>} />
            <Route path="permits/:id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY_READ]}><ModuleGuard module="workPermits"><CriticalTaskPermitDetail /></ModuleGuard></RestrictedRoute>} />
            <Route path="permits/:id/edit" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]} allowWorkDocumentManagers><ModuleGuard module="workPermits"><CriticalTaskPermitCreateFlow /></ModuleGuard></RestrictedRoute>} />

            {/* Templates */}
            <Route path="templates/:id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]}><TemplateDetail /></RestrictedRoute>} />

            {/* Forms */}
            <Route path="forms/new" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]}><FormEditor /></RestrictedRoute>} />
            <Route path="forms/:id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]}><FormDetail /></RestrictedRoute>} />
            <Route path="forms/:id/edit" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_LIBRARY]}><FormEditor /></RestrictedRoute>} />
          </Route>

          {/* === Clients & Providers (split views) === */}
          <Route path="clients" element={<RestrictedRoute blockedRoles={[UserRole.COMPANY_ADMIN, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Companies key="clients" lockedCompanyType="cliente" /></RestrictedRoute>} />
          <Route path="providers" element={<RestrictedRoute blockedRoles={[UserRole.COMPANY_ADMIN, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Companies key="providers" lockedCompanyType="proveedor" /></RestrictedRoute>} />
          {/* Mis Proveedores — dedicated page for client roles to review the
              providers linked to their company. Replaces the inline card
              that used to live inside CompanyDetail. Security is excluded
              since they don't manage commercial relationships. */}
          <Route path="my-providers" element={<RestrictedRoute blockedRoles={[UserRole.SECURITY]}><MyProviders /></RestrictedRoute>} />
          <Route path="my-providers/:providerId" element={<RestrictedRoute blockedRoles={[UserRole.SECURITY]}><ProviderInMyCompany /></RestrictedRoute>} />

          {/* === CONTEXT: COMPANIES (Platform Admin Only) ===
            /companies/:companyId for drill-down, /companies/new for creation.
            List views are at /clients and /providers.
          */}
          <Route path="companies">
            {/* Bare /companies — unlocked, all-companies view. Super_admin
                only: it hosts the Soporte Monitor coverage dashboard
                (missing-assignment list + per-Soporte-Monitor breakdown),
                which is scoped to super_admin. Clientes/Proveedores keep
                their own locked lists at /clients and /providers. */}
            <Route index element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.COMPANY_ADMIN, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Companies /></RestrictedRoute>} />
            <Route path="new" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.COMPANY_ADMIN, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><CompanyForm /></RestrictedRoute>} />
            <Route path="unlinked-users">
              <Route index element={<RestrictedRoute blockedRoles={[UserRole.COMPANY_ADMIN, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Users variant="unlinked" /></RestrictedRoute>} />
              {/* No detail page — a user with zero company assignments has
                  nothing else to show, so the list links straight to edit,
                  which is also where a company can actually be assigned
                  (see systemBasePath in Users.tsx for why /admins/:id/edit
                  couldn't be reused here: it forces UserForm's super_admin
                  editing context, which hides company assignment). */}
              <Route path=":id/edit" element={<RestrictedRoute blockedRoles={[UserRole.COMPANY_ADMIN, UserRole.CLIENT_APPROVER, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><UserForm /></RestrictedRoute>} />
            </Route>

            {/* Company Detail & Management — platform roles pass through; company_admin
                is also let through when :companyId is their own company (see
                CompanyContextGuard), so this subtree renders the same way whether
                reached via /my-company or via a direct /companies/:id link. Anyone
                else, or company_admin viewing a company that isn't theirs, is blocked. */}
            <Route path=":companyId" element={<CompanyContextGuard><Outlet /></CompanyContextGuard>}>
              {/* Bare company URL → company-scoped Resumen (which now hosts the company detail content). */}
              <Route index element={<Navigate to="resumen" replace />} />
              <Route path="edit" element={<CompanyForm />} />
              {/* Usuarios + Invitaciones + Verificaciones share the tabbed shell. */}
              <Route path="users" element={<UsersAndVerificationsTabs activeTab="usuarios" />} />
              <Route path="invitaciones" element={<UsersAndVerificationsTabs activeTab="invitaciones" />} />
              <Route path="batch-registration" element={<BatchUserRegistration />} />

              {/* Drill-down into specific company resources */}
              <Route path="users/new" element={<CompanyUserForm />} />
              <Route path="users/:id" element={<CompanyUserDetail />} />
              <Route path="users/:id/edit" element={<CompanyUserForm />} />
              <Route path="visitas" element={<GuestVisits />} />
              <Route path="departments" element={<CompanyDepartmentsPage />} />
              <Route path="cursos" element={<ModuleGuard module="capacitaciones"><CompanyCoursesPage /></ModuleGuard>} />

              <Route path="library" element={<ModuleGuard module="workPermits"><LibraryTabs /></ModuleGuard>} />
              {/* Service Cards (requires workPermits module) */}
              <Route path="service-cards/new" element={<ModuleGuard module="workPermits"><ServiceCardForm /></ModuleGuard>} />
              <Route path="service-cards/:id" element={<ModuleGuard module="workPermits"><ServiceCardDetail /></ModuleGuard>} />
              <Route path="service-cards/:id/edit" element={<ModuleGuard module="workPermits"><ServiceCardForm /></ModuleGuard>} />
              {/* Initial Evaluations (requires workPermits module) */}
              <Route path="evaluations/new" element={<ModuleGuard module="workPermits"><InitialEvaluationCreateFlow /></ModuleGuard>} />
              <Route path="evaluations/:id" element={<ModuleGuard module="workPermits"><InitialEvaluationDetail /></ModuleGuard>} />
              <Route path="evaluations/:id/edit" element={<ModuleGuard module="workPermits"><InitialEvaluationCreateFlow /></ModuleGuard>} />
              {/* Critical Task Permits (requires workPermits module) */}
              <Route path="permits/new" element={<ModuleGuard module="workPermits"><CriticalTaskPermitCreateFlow /></ModuleGuard>} />
              <Route path="permits/:id" element={<ModuleGuard module="workPermits"><CriticalTaskPermitDetail /></ModuleGuard>} />
              <Route path="permits/:id/edit" element={<ModuleGuard module="workPermits"><CriticalTaskPermitCreateFlow /></ModuleGuard>} />

              <Route path="verifications" element={<CompanyVerificationsPage />} />
              <Route path="verifications/usuarios" element={<UserVerificationsPage />} />
              <Route path="verifications/proveedores" element={<ProviderVerificationsPage />} />
              <Route path="verifications/bulk/new" element={<BulkVerificationWizard />} />
              <Route path="solicitudes-remocion" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_REMOVAL_REQUESTS]}><UserRemovalRequestsPage /></RestrictedRoute>} />
              {/* Legacy /settings — redirect to the new /configuracion. */}
              <Route path="settings" element={<Navigate to="../configuracion" relative="path" replace />} />
              <Route path="incident-reports" element={<IncidentReports />} />
              <Route path="attention-calls" element={<AttentionCalls />} />
              <Route path="support-tickets" element={<SupportTickets />} />
              {/* Company-scoped Información for super_admin/safety_staff —
                  same tabbed page as /my-company/informacion (company_admin),
                  reached from CompanyDetail's stat cards rather than a
                  sidebar item; the platform sidebar has no entry for it. */}
              <Route path="informacion" element={<CompanyInformacionPage />} />
              {/* Legacy /logs — redirect to the new /historial. */}
              <Route path="logs" element={<Navigate to="../historial" relative="path" replace />} />

              {/* Platform 8-item sidebar — company-scoped variants (super_admin / safety_staff).
                  /resumen renders the existing CompanyDetail (the page that used to live at the
                  bare /companies/:id index). Future: replace with a real per-company KPI summary. */}
              <Route path="resumen" element={<CompanyDetail />} />
              <Route path="documentos" element={<CompanyDocumentsPage />} />
              <Route path="proveedores" element={<MyProviders />} />
              <Route path="proveedores/:providerId" element={<ProviderInMyCompany />} />
              {/* Proveedor companies → "Mis Clientes" page (symmetric
                  to MyProviders but flipped: lists client companies
                  this proveedor serves). */}
              <Route path="clientes" element={<MyClients />} />
              <Route path="supervisor-transfers" element={<SupervisorTransfersPage />} />
              {/* Mensajes inside the company shell — same render as
                  the global /mensajes route, just keeps the URL
                  scoped so the sidebar context badge + Dashboard
                  shortcut stay visible. */}
              <Route path="mensajes" element={<Messages />} />
              {/* Marketplace inside the company shell — preserves the
                  company context so the sidebar badge / Dashboard shortcut
                  remain visible. The MarketplacePage reads the urlCompanyId
                  param to scope its "Publicar trabajo" action automatically
                  (no per-post company picker needed). */}
              <Route path="marketplace" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_MARKETPLACE]}><ModuleGuard module="marketplace"><MarketplacePage /></ModuleGuard></RestrictedRoute>} />
              <Route path="en-proceso" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><EnProcesoPage /></RestrictedRoute>} />
              <Route path="historial" element={<ActivityLog />} />
              {/* /notificaciones intentionally not exposed in company context — notifications stay global */}
              <Route path="capacitaciones" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><CapacitacionesPage /></RestrictedRoute>} />
              <Route path="configuracion" element={<CompanySettings />} />
              {/* Help center inside the company shell — keeps the company-scoped
                  sidebar visible instead of dropping the user back to global. */}
              <Route path="ayuda" element={<HelpCenter />} />
            </Route>
          </Route>

          {/* ==================== INVITACIONES (Global, platform roles) ====================
              Cross-company view of pending invitations for super_admin/safety_staff,
              with a company picker (defaults to "todas las empresas"). Company-scoped
              equivalent lives inside UsersAndVerificationsTabs (/my-company/invitaciones,
              /companies/:companyId/invitaciones). */}
          <Route path="invitaciones" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><InvitationsList showCompanyPicker /></RestrictedRoute>} />

          {/* ==================== ALL USERS DASHBOARD (super_admin + safety_staff, portfolio-scoped) ==================== */}
          <Route path="todos-los-usuarios" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><AllUsersDashboard /></RestrictedRoute>} />

          {/* ==================== ADMIN USERS (SUPER_ADMIN only) ==================== */}
          <Route path="admins">
            <Route index element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Users filterRole={UserRole.SUPER_ADMIN} /></RestrictedRoute>} />
            <Route path="new" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><UserForm /></RestrictedRoute>} />
            <Route path=":id" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><CompanyUserDetail /></RestrictedRoute>} />
            <Route path=":id/edit" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><UserForm /></RestrictedRoute>} />
          </Route>

          {/* ==================== SAFETY STAFF USERS (PLATFORM_ROLES) ====================
              Canonical path is /soporte-monitor. The legacy /safety_staff routes
              redirect for backward compatibility (old links / bookmarks). Listing +
              creation is restricted to super_admin per the latest scope decision —
              safety_staff users can no longer self-provision peers. */}
          <Route path="soporte-monitor">
            <Route index element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><Users filterRole={UserRole.SAFETY_STAFF} /></RestrictedRoute>} />
            <Route path="new" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><UserForm /></RestrictedRoute>} />
            <Route path=":id" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><CompanyUserDetail /></RestrictedRoute>} />
            <Route path=":id/edit" element={<RestrictedRoute blockedRoles={[UserRole.SAFETY_STAFF, UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SECURITY]}><UserForm /></RestrictedRoute>} />
          </Route>
          {/* Legacy redirects — keep until callers are migrated */}
          <Route path="safety_staff" element={<Navigate to="/soporte-monitor" replace />} />
          <Route path="safety_staff/new" element={<Navigate to="/soporte-monitor/new" replace />} />

          {/* ==================== CONTRACTORS ==================== */}
          <Route path="contractors">
            <Route index element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><Contractors /></RestrictedRoute>} />
            <Route path="new" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><ContractorForm /></RestrictedRoute>} />
            <Route path="search" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><ContractorSearch /></RestrictedRoute>} />
            <Route path=":id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><ContractorDetail /></RestrictedRoute>} />
            <Route path=":id/edit" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><ContractorForm /></RestrictedRoute>} />
          </Route>

          {/* Supervised Contractors (Sub-lists) */}
          <Route path="supervised-contractors">
            <Route index element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><SupervisedContractors /></RestrictedRoute>} />
            <Route path="new" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><SupervisedContractorForm /></RestrictedRoute>} />
            <Route path=":id" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><SupervisedContractorDetail /></RestrictedRoute>} />
            <Route path=":id/edit" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CONTRACTOR_MGMT]}><SupervisedContractorForm /></RestrictedRoute>} />
          </Route>

          {/* ==================== MY WORK PERMITS (Contractor users) ==================== */}
          {/* Contractor identity is now company_admin / client_staff at a
              proveedor company; the page filters by assignedToMe so any
              non-platform / non-security role can land here safely. */}
          <Route path="my-work-permits" element={<RestrictedRoute blockedRoles={[UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.SECURITY]}><CriticalTaskPermits assignedToMe /></RestrictedRoute>} />

          {/* ==================== TEAMS (Field Operations) ==================== */}
          <Route path="my-teams">
            <Route index element={<RestrictedRoute blockedRoles={[...BLOCK_NON_TEAMS]}><MyTeams /></RestrictedRoute>} />
            <Route path=":teamId/members" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_TEAMS]}><TeamMembers /></RestrictedRoute>} />
          </Route>

          {/* ==================== TRAINING & COURSES ==================== */}
          <Route path="courses">
            <Route index element={<RestrictedRoute blockedRoles={[UserRole.SECURITY]}><Courses /></RestrictedRoute>} />
            <Route path=":courseId/enrollments" element={<RestrictedRoute blockedRoles={[UserRole.SECURITY]}><CourseEnrollments /></RestrictedRoute>} />
            <Route path="progress" element={<RestrictedRoute blockedRoles={[UserRole.SECURITY]}><ContractorProgressPage /></RestrictedRoute>} />
            <Route path="bulk-sync" element={<RestrictedRoute blockedRoles={[UserRole.CLIENT_APPROVER, UserRole.SECURITY]}><BulkSync /></RestrictedRoute>} />
          </Route>

          {/* ==================== GLOBAL VERIFICATIONS ==================== */}

          {/* ==================== USER VERIFICATIONS ====================
              Per-company verifications only apply to client_staff. Block
              the rest so company_admin / client_approver / security don't
              see "upload" prompts that don't pertain to them. */}
          <Route path="my-verifications" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CLIENT_STAFF]}><UserVerificationsDashboard /></RestrictedRoute>} />

          {/* ==================== USER VERIFICATION AUDIT LOG ==================== */}
          <Route path="users/:userId/verification-log" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_PLATFORM]}><UserVerificationAuditLog /></RestrictedRoute>} />

          {/* ==================== APPROVER (CLIENT_APPROVER only) ==================== */}
          <Route path="approver" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_APPROVER]}><Outlet /></RestrictedRoute>}>
            <Route path="dashboard" element={<ApproverDashboard />} />
            <Route path="company" element={<ApproverCompanyDetail />} />
            <Route path="work-permits" element={<ApproverWorkPermits />} />
            <Route path="work-permits/:id" element={<ApproverWorkPermitDetail />} />
          </Route>

          {/* ==================== CLIENT STAFF (Personal) ==================== */}
          <Route path="staff" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_CLIENT_STAFF]}><Outlet /></RestrictedRoute>}>
            <Route path="inicio" element={<StaffInicio />} />
            <Route path="consulta" element={<StaffConsulta />} />
            <Route path="tareas" element={<StaffTareas />} />
            <Route path="historial" element={<StaffHistorial />} />
            <Route path="team" element={<StaffTeam />} />
            <Route path="incident-reports" element={<IncidentReports />} />
            <Route path="dashboard" element={<ClientStaffDashboard />} />
            <Route path="permits" element={<CriticalTaskPermits assignedToMe />} />
            <Route path="evaluations" element={<InitialEvaluations />} />
            <Route path="service-cards" element={<ServiceCards hideInactive />} />
            {/* Supervisor (client_staff with manages.length > 0) drill-down
                into a direct report's profile. The components fall back to
                user.company._id when :companyId is absent from the URL; the
                BE's canViewUser / canUpdateUser enforce the manages check so
                a non-supervisor staff user opening these routes manually
                gets a 403. */}
            <Route path="users/:id" element={<CompanyUserDetail />} />
            <Route path="users/:id/edit" element={<CompanyUserForm />} />
            {/* "Admin Proveedor" bypass — a provider's own (non-external)
                company_admin, currently lent out and active at a client as
                client_staff, still needs to manage their OWN provider
                company's client relationships + team verifications without
                switching active company (which would just bounce them to
                their possibly-planless provider dashboard). MyClients falls
                back to getHomeProviderAdminCompany(user) for its companyId
                when reached here (no :companyId in the URL) — see the
                comment in MyClients.tsx. Not shown to a regular colaborador:
                StaffInicio only links here when that helper returns non-null. */}
            <Route path="mi-proveedor/clientes" element={<MyClients />} />
            {/* Información (accidentes / sanciones / tickets) para el admin
                de una proveedora parado dentro de un cliente. Es la misma
                página tabulada de /my-company/informacion; el alcance lo pone
                el BE, que para este llamador filtra a los registros donde
                participa alguien de SU empresa (ver
                getCallerOwnProviderCompanyId). Un colaborador normal no tiene
                entrada de menú aquí y el BE le devolvería solo lo suyo. */}
            <Route path="informacion" element={<CompanyInformacionPage />} />
          </Route>

          {/* ==================== CONTRACTOR (provider workspace) ====================
              The legacy contratista_* roles have been folded into
              company_admin / client_staff at proveedor companies. The route
              guard filters out platform + security; the BE scopes data so
              users at cliente companies see nothing meaningful here. */}
          <Route path="contractor" element={<RestrictedRoute blockedRoles={[UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.SECURITY, UserRole.CLIENT_APPROVER]}><Outlet /></RestrictedRoute>}>
            <Route path="dashboard" element={<ContractorDashboard />} />
            <Route path="permits" element={<CriticalTaskPermits assignedToMe />} />
            <Route path="evaluations" element={<InitialEvaluations />} />
            <Route path="service-cards" element={<ServiceCards hideInactive />} />
          </Route>

          {/* ==================== SECURITY GUARD ==================== */}
          <Route path="guard">
            <Route path="users" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SECURITY]}><SecurityUsers /></RestrictedRoute>} />
          </Route>
          <Route path="security/guest-search" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_GUEST_ACCESS]}><GuestSearch /></RestrictedRoute>} />
          {/* New "Seguridad — Recepción" sidebar */}
          <Route path="seguridad/inicio" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SECURITY]}><SecurityInicioPage /></RestrictedRoute>} />
          <Route path="seguridad/consulta" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SECURITY]}><SecurityConsultaPage /></RestrictedRoute>} />
          <Route path="seguridad/visitas" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SECURITY]}><GuestVisits /></RestrictedRoute>} />
          <Route path="seguridad/qr" element={<RestrictedRoute blockedRoles={[...BLOCK_NON_SECURITY]}><QrScanner /></RestrictedRoute>} />

          {/* Marketplace routes removed — feature planned for future */}

          {/* Top-level /incident-reports — accessible to every authenticated
              role. The BE already scopes the list per role (super_admin sees
              all, company_admin sees their company, everyone else only
              incidents they reported or are involved in), so we don't gate
              it here. */}
          <Route path="incident-reports" element={<IncidentReports />} />

          {/* Task detail — same audience as the task list (no security/approver gate). */}
          <Route path="task/:id" element={<RestrictedRoute blockedRoles={[UserRole.SECURITY, UserRole.CLIENT_APPROVER]}><TaskDetail /></RestrictedRoute>} />

          {/* 404 - Catch all for authenticated users */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* 404 - Catch all for public */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>

      {/* Global Modals */}
      {showCookieConsent && <CookieConsent onAccept={handleCookieConsent} />}
      <SessionExpiredModal
        open={sessionExpired}
        onLogin={handleSessionExpiredLogin}
      />
      <NetworkBlockedModal
        open={networkBlocked}
        onClose={() => setNetworkBlocked(false)}
      />
      </GlobalLoaderProvider>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeWrapper>
          <AuthProvider>
            <PageTitleProvider>
              <AppContent />
            </PageTitleProvider>
          </AuthProvider>
        </ThemeWrapper>
        {/* React Query DevTools - only visible in development */}
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        )}
      </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;